import { useCallback, useEffect, useState } from "react";
import { FiChevronDown, FiTrash2, FiUsers } from "react-icons/fi";
import { HashLoader } from "react-spinners";
import MainLayout from "../../Layouts/MainLayout";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import ApproveDenyModal from "../../components/RootElements/ApproveDenyModal";
import { GetAllUserRoles, UpsertUserRole, DeleteUserRole } from "../../lib/ApiHandler/UserRoleApi";
import { APP_ADMIN_ROLE, APP_USER_ROLE, APP_SUPER_ADMIN_ROLE, TORK_READER_ROLE, TORK_ADMIN_ROLE } from "../../lib/Constants";
import { NotifyError, NotifySuccess } from "../../lib/Notify/notify";

const ROLE_META = [
    {
        id: APP_USER_ROLE,
        label: "Användare",
        description: "Kan logga in i KABOL och se kartan, mellanlager och instruktioner.",
        activeClasses: "bg-blue-600 border-blue-600 text-white hover:bg-blue-700",
        dotClasses: "bg-blue-600",
    },
    {
        id: APP_ADMIN_ROLE,
        label: "Karta - Admin",
        description: "Kan redigera former på kartan och ta bort markeringar.",
        activeClasses: "bg-amber-400 border-amber-400 text-amber-950 hover:bg-amber-500",
        dotClasses: "bg-amber-500",
    },
    {
        id: APP_SUPER_ADMIN_ROLE,
        label: "Superadmin",
        description: "Kan hantera användares åtkomst till applikationen.",
        activeClasses: "bg-red-600 border-red-600 text-white hover:bg-red-700",
        dotClasses: "bg-red-600",
    },
    {
        id: TORK_READER_ROLE,
        label: "Tork - Läsare",
        description: "Kan se Torkplanering (ordrar, torkar att tömma, torkar, historik) utan att ändra någonting.",
        activeClasses: "bg-green-600 border-green-600 text-white hover:bg-green-700",
        dotClasses: "bg-green-600",
    },
    {
        id: TORK_ADMIN_ROLE,
        label: "Tork - Admin",
        description: "Full åtkomst till Torkplanering — kan skapa, redigera och ta bort ordrar, torkar och noteringar.",
        activeClasses: "bg-purple-600 border-purple-600 text-white hover:bg-purple-700",
        dotClasses: "bg-purple-600",
    },
];

const PILL_INACTIVE_CLASSES = "bg-white border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600";

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

// UpsertUserRole's callback fires even on HTTP errors (it never checks res.ok),
// so success is detected by the shape of the response body.
const isValidUserRoleResult = (result) =>
    result && typeof result === "object" && Array.isArray(result.roles);

const RolePill = ({ meta, active, disabled, onClick }) => (
    <button
        type="button"
        title={`${meta.label}: ${meta.description}`}
        disabled={disabled}
        onClick={onClick}
        className={`px-2.5 py-1 rounded-full border text-xs font-medium transition select-none whitespace-nowrap
            ${active ? meta.activeClasses : PILL_INACTIVE_CLASSES}
            ${disabled ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
    >
        {meta.label}
    </button>
);

const RoleLegend = () => {
    const [open, setOpen] = useState(false);
    return (
        <div className="mb-6">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
                <FiChevronDown className={`transition-transform ${open ? "rotate-180" : ""}`} />
                Vad betyder rollerna?
            </button>
            {open && (
                <div className="mt-3 bg-white rounded-lg shadow p-4 space-y-2">
                    {ROLE_META.map(meta => (
                        <div key={meta.id} className="flex items-start gap-2 text-sm">
                            <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${meta.dotClasses}`} />
                            <span>
                                <span className="font-medium text-gray-800">{meta.label}</span>
                                <span className="text-gray-500"> — {meta.description}</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const UserRoles = ({ jwtToken }) => {
    const [userRoles, setUserRoles] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [newRoles, setNewRoles] = useState([APP_USER_ROLE]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [confirmDeleteEmail, setConfirmDeleteEmail] = useState(null);
    const [pendingKeys, setPendingKeys] = useState(new Set());

    const fetchUserRoles = useCallback(() => {
        GetAllUserRoles(jwtToken, (result) => {
            setUserRoles(result);
            setLoading(false);
        });
    }, [jwtToken]);

    useEffect(() => {
        fetchUserRoles();
    }, [fetchUserRoles]);

    const handleAddUser = () => {
        const email = newEmail.trim().toLowerCase();
        if (!EMAIL_REGEX.test(email)) {
            NotifyError("Ange en giltig e-postadress.");
            return;
        }
        if (newRoles.length === 0) {
            NotifyError("Välj minst en roll.");
            return;
        }
        if (userRoles.some(u => u.email === email)) {
            NotifyError("Användaren finns redan i listan.");
            return;
        }
        setAdding(true);
        UpsertUserRole(email, newRoles, jwtToken, (result) => {
            setAdding(false);
            if (isValidUserRoleResult(result)) {
                NotifySuccess(`${email} tillagd.`);
                setNewEmail("");
                setNewRoles([APP_USER_ROLE]);
                fetchUserRoles();
            } else {
                NotifyError("Kunde inte lägga till användaren.");
            }
        });
    };

    const handleToggleRole = (email, currentRoles, roleId) => {
        const key = `${email}:${roleId}`;
        if (pendingKeys.has(key)) return;
        const updatedRoles = currentRoles.includes(roleId)
            ? currentRoles.filter(r => r !== roleId)
            : [...currentRoles, roleId];

        setPendingKeys(prev => new Set(prev).add(key));
        setUserRoles(prev => prev.map(u => u.email === email ? { ...u, roles: updatedRoles } : u));

        UpsertUserRole(email, updatedRoles, jwtToken, (result) => {
            setPendingKeys(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            if (isValidUserRoleResult(result)) {
                setUserRoles(prev => prev.map(u => u.email === email ? { ...u, roles: result.roles } : u));
            } else {
                NotifyError("Kunde inte uppdatera roller. Listan laddas om.");
                fetchUserRoles();
            }
        });
    };

    const handleConfirmDelete = () => {
        const email = confirmDeleteEmail;
        setConfirmDeleteEmail(null);
        DeleteUserRole(email, jwtToken, (ok) => {
            if (ok) {
                setUserRoles(prev => prev.filter(u => u.email !== email));
                NotifySuccess(`${email} borttagen.`);
            } else {
                NotifyError("Kunde inte ta bort användaren.");
            }
        });
    };

    return (
        <MainLayout>
            <div className="container p-12 max-h-dvh overflow-y-scroll">
                <h1 className="text-2xl font-semibold text-gray-800 mb-2">Hantera användarbehörigheter</h1>

                <RoleLegend />

                {/* Add new user */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h2 className="text-lg font-medium text-gray-700 mb-4">Lägg till användare</h2>
                    <div className="flex flex-col gap-4 md:flex-row md:items-end">
                        <div className="flex-1">
                            <label className="block text-sm text-gray-600 mb-1">E-postadress</label>
                            <input
                                type="email"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="user@example.com"
                                value={newEmail}
                                onChange={e => setNewEmail(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && handleAddUser()}
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Roller</label>
                            <div className="flex flex-wrap gap-2">
                                {ROLE_META.map(meta => (
                                    <RolePill
                                        key={meta.id}
                                        meta={meta}
                                        active={newRoles.includes(meta.id)}
                                        onClick={() =>
                                            setNewRoles(prev =>
                                                prev.includes(meta.id)
                                                    ? prev.filter(r => r !== meta.id)
                                                    : [...prev, meta.id]
                                            )
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                        <PrimaryButton onClick={handleAddUser} props={{ disabled: adding }}>
                            Lägg till
                        </PrimaryButton>
                    </div>
                </div>

                {/* User list */}
                {loading ? (
                    <div className="flex flex-col items-center gap-4 py-16">
                        <HashLoader color="#2563eb" size={40} />
                        <p className="text-gray-500 text-sm">Laddar användare...</p>
                    </div>
                ) : userRoles.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-10 flex flex-col items-center gap-2 text-center">
                        <FiUsers className="w-8 h-8 text-gray-300" />
                        <p className="text-gray-700 font-medium">Inga användare hittades.</p>
                        <p className="text-gray-500 text-sm">Lägg till en användare ovan för att ge behörighet.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-6 py-3 text-gray-600 font-medium">E-post</th>
                                    <th className="px-4 py-3 text-gray-600 font-medium">Roller</th>
                                    <th className="px-4 py-3 w-12"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {userRoles.map(user => (
                                    <tr key={user.email} className="border-b hover:bg-gray-50">
                                        <td className="px-6 py-3 text-gray-800">{user.email}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1.5">
                                                {ROLE_META.map(meta => (
                                                    <RolePill
                                                        key={meta.id}
                                                        meta={meta}
                                                        active={user.roles?.includes(meta.id) ?? false}
                                                        disabled={pendingKeys.has(`${user.email}:${meta.id}`)}
                                                        onClick={() => handleToggleRole(user.email, user.roles ?? [], meta.id)}
                                                    />
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                type="button"
                                                title="Ta bort användare"
                                                onClick={() => setConfirmDeleteEmail(user.email)}
                                                className="text-gray-400 hover:text-red-600 transition"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {confirmDeleteEmail && (
                    <ApproveDenyModal
                        onAccept={handleConfirmDelete}
                        onDeny={() => setConfirmDeleteEmail(null)}
                    >
                        Vill du ta bort alla roller för {confirmDeleteEmail}?
                    </ApproveDenyModal>
                )}
            </div>
        </MainLayout>
    );
};

export default UserRoles;
